import { useState, useEffect, useRef } from "react";
import { Search, Plus, User, Building2 } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { ImageWithFallback } from "./components/figma/ImageWithFallback";
import { EventCard } from "./components/EventCard";
import { EventDetails } from "./components/EventDetails";
import { EventFilters } from "./components/EventFilters";
import { TicketSelection } from "./components/TicketSelection";
import { Cart } from "./components/Cart";
import { Checkout } from "./components/Checkout";
import { OrderConfirmation } from "./components/OrderConfirmation";
import { CulturalStory } from "./components/CulturalStory";
import { CreateEvent } from "./components/CreateEvent";
import { Footer } from "./components/Footer";
import { CultureSection } from "./components/CultureSection";
import { CultureBackground } from "./components/CultureBackground";
import { BackgroundGrid } from "./components/BackgroundGrid";
import { AdminAuth } from "./components/AdminAuth";
import { AdminDashboard } from "./components/AdminDashboard";
import { UserAuth } from "./components/UserAuth";
import { UserProfile } from "./components/UserProfile";
import { OrganizerAuth } from "./components/OrganizerAuth";
import { OrganizerDashboard } from "./components/OrganizerDashboard";
import { OrganizerPage } from "./components/OrganizerPage";
import { PaymentSuccess } from "./components/PaymentSuccess";
import { PaymentCancel } from "./components/PaymentCancel";
import { CGVPage } from "./components/CGVPage";
import { CGUPage } from "./components/CGUPage";
import { ContactPage } from "./components/ContactPage";
import { AboutPage } from "./components/AboutPage";
import { CalendarButton } from "./components/CalendarButton";
import { SEO, EventSEO, OrganizerSEO } from "./components/SEO";
import { Toaster } from "./components/ui/sonner";
import axios from "axios";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [currentView, setCurrentView] = useState("home");
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedOrganizerId, setSelectedOrganizerId] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isCreateEventOpen, setIsCreateEventOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("events");
  const [lastOrderId, setLastOrderId] = useState(null);
  
  // Admin states
  const [showAdminAuth, setShowAdminAuth] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [isAdmin, setIsAdmin] = useState(() => {
    return localStorage.getItem("tika_admin_auth") === "true";
  });
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [logoClickTimer, setLogoClickTimer] = useState(null);

  // User authentication states
  const [showUserAuth, setShowUserAuth] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => {
    const savedUser = localStorage.getItem("tika_user");
    return savedUser ? JSON.parse(savedUser) : null;
  });

  // Organizer states
  const [showOrganizerAuth, setShowOrganizerAuth] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return !!params.get("reset_token");
  });
  const [showOrganizerDashboard, setShowOrganizerDashboard] = useState(false);
  const [currentOrganizer, setCurrentOrganizer] = useState(() => {
    const savedOrganizer = localStorage.getItem("tika_organizer");
    return savedOrganizer ? JSON.parse(savedOrganizer) : null;
  });

  // Events from backend
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilters, setActiveFilters] = useState({});

  // Handle URL-based routing for events and organizers
  useEffect(() => {
    const handleUrlRouting = () => {
      const path = window.location.pathname;
      
      // Check for payment success URL
      if (path === "/payment/success") {
        setCurrentView("payment-success");
        return;
      }
      
      // Check for payment cancel URL
      if (path === "/payment/cancel") {
        setCurrentView("payment-cancel");
        return;
      }
      
      // Check for event URL
      const eventMatch = path.match(/^\/event\/(.+)$/);
      if (eventMatch) {
        const eventId = eventMatch[1];
        setSelectedEventId(eventId);
        setCurrentView("event-details");
        return;
      }
      
      // Check for organizer URL
      const organizerMatch = path.match(/^\/organizer\/(.+)$/);
      if (organizerMatch) {
        const organizerId = organizerMatch[1];
        setSelectedOrganizerId(organizerId);
        setCurrentView("organizer-page");
        return;
      }
    };
    
    handleUrlRouting();
    window.addEventListener('popstate', handleUrlRouting);
    
    return () => window.removeEventListener('popstate', handleUrlRouting);
  }, []);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async (retries = 2) => {
    try {
      const params = new URLSearchParams();
      
      if (activeFilters.city) params.append('city', activeFilters.city);
      if (activeFilters.min_price) params.append('min_price', activeFilters.min_price);
      if (activeFilters.max_price) params.append('max_price', activeFilters.max_price);
      if (activeFilters.date_from) params.append('date_from', activeFilters.date_from);
      if (activeFilters.date_to) params.append('date_to', activeFilters.date_to);
      if (activeFilters.hide_sold_out) params.append('hide_sold_out', 'true');
      
      const queryString = params.toString();
      const url = queryString ? `${API}/events?${queryString}` : `${API}/events`;
      
      const response = await axios.get(url, { timeout: 15000 });
      if (response.data && response.data.length >= 0) {
        setEvents(response.data);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des événements:", error);
      if (retries > 0) {
        setTimeout(() => fetchEvents(retries - 1), 2000);
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setActiveFilters(newFilters);
  };
  
  // Refetch events when filters change (not on initial load)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters.city, activeFilters.min_price, activeFilters.max_price, activeFilters.date_from, activeFilters.date_to, activeFilters.hide_sold_out]);

  const filteredEvents = events.filter((event) => {
    // Handle category mapping
    let eventCategory = event.category;
    
    // Map old categories to new ones
    if (eventCategory === "Hip Hop" || eventCategory === "RNB") {
      eventCategory = "Hip-Hop/RNB";
    } else if (eventCategory === "Dancehall") {
      eventCategory = "Dancehall/Caraïbes";
    }
    
    const matchesCategory = selectedCategory === "all" || eventCategory === selectedCategory;
    const matchesSearch =
      searchQuery === "" ||
      event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.artist.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const selectedEvent = events.find((e) => e.id === selectedEventId) || null;

  const handleViewDetails = (eventId) => {
    setSelectedEventId(eventId);
    setCurrentView("event-details");
    // Update URL without reload
    window.history.pushState({}, '', `/event/${eventId}`);
  };

  const handleSelectTickets = () => {
    setCurrentView("ticket-selection");
  };

  const handleAddToCart = (items) => {
    const newCartItems = [...cartItems, ...items];
    setCartItems(newCartItems);
    // Immediately go to checkout after adding items
    setCurrentView("checkout");
  };

  const handleRemoveFromCart = (index) => {
    setCartItems(cartItems.filter((_, i) => i !== index));
  };

  const handleCheckout = () => {
    if (cartItems.length > 0) {
      setCurrentView("checkout");
    }
  };

  const handleCompleteOrder = (orderId) => {
    setLastOrderId(orderId);
    setCurrentView("confirmation");
    // Clear event URL after order
    window.history.pushState({}, '', '/');
  };

  const handleReturnHome = () => {
    setCartItems([]);
    setCurrentView("home");
    setSelectedEventId(null);
    setLastOrderId(null);
    // Reset URL to home
    window.history.pushState({}, '', '/');
  };

  const handleBackToEvent = () => {
    setCurrentView("event-details");
  };

  const handleFooterNavigate = (page) => {
    if (page === "home") {
      setCurrentView("home");
      setActiveTab("events");
      window.history.pushState({}, '', '/');
    } else if (page === "culture") {
      setCurrentView("home");
      setActiveTab("culture");
    } else if (page === "cgv") {
      setCurrentView("cgv");
    } else if (page === "cgu") {
      setCurrentView("cgu");
    } else if (page === "contact") {
      setCurrentView("contact");
    } else if (page === "about") {
      setCurrentView("about");
    } else if (page === "privacy") {
      setCurrentView("cgu"); // Redirect to CGU for now
    }
  };

  // Admin functions
  const handleLogoClick = () => {
    setLogoClickCount((prev) => prev + 1);
    
    if (logoClickTimer) {
      clearTimeout(logoClickTimer);
    }
    
    const timer = setTimeout(() => {
      if (logoClickCount >= 2) {
        // Triple click detected
        if (isAdmin) {
          setShowAdminDashboard(true);
        } else {
          setShowAdminAuth(true);
        }
      }
      setLogoClickCount(0);
    }, 500);
    
    setLogoClickTimer(timer);
  };

  const handleAdminAuthenticated = () => {
    setIsAdmin(true);
    setShowAdminAuth(false);
    setShowAdminDashboard(true);
  };

  const handleAdminLogout = () => {
    setIsAdmin(false);
    setShowAdminDashboard(false);
  };

  // User authentication handlers
  const handleUserAuthenticated = (user) => {
    setCurrentUser(user);
    setShowUserAuth(false);
  };

  const handleUserLogout = () => {
    setCurrentUser(null);
  };

  // Organizer handlers
  const handleOrganizerAuthenticated = (organizer) => {
    setCurrentOrganizer(organizer);
    setShowOrganizerAuth(false);
    setShowOrganizerDashboard(true);
  };

  const handleOrganizerLogout = () => {
    setCurrentOrganizer(null);
    setShowOrganizerDashboard(false);
  };

  const handleOrganizerUpdate = (updatedOrganizer) => {
    setCurrentOrganizer(updatedOrganizer);
  };

  const handleViewOrganizer = (organizerId) => {
    setSelectedOrganizerId(organizerId);
    setCurrentView("organizer-page");
    window.history.pushState({}, '', `/organizer/${organizerId}`);
  };

  // Payment success page
  if (currentView === "payment-success") {
    return (
      <PaymentSuccess
        onComplete={(orderId) => {
          setLastOrderId(orderId);
          setCurrentView("confirmation");
          window.history.pushState({}, '', '/');
        }}
      />
    );
  }

  // Payment cancel page
  if (currentView === "payment-cancel") {
    return (
      <PaymentCancel
        onBack={() => {
          setCurrentView("home");
          window.history.pushState({}, '', '/');
        }}
      />
    );
  }

  // Organizer public page view
  if (currentView === "organizer-page" && selectedOrganizerId) {
    return (
      <OrganizerPage
        organizerId={selectedOrganizerId}
        onBack={() => {
          setCurrentView("home");
          setSelectedOrganizerId(null);
          window.history.pushState({}, '', '/');
        }}
        onViewEvent={handleViewDetails}
        currentUser={currentUser}
      />
    );
  }

  if (currentView === "about") {
    return <AboutPage onBack={() => setCurrentView("home")} />;
  }

  if (currentView === "cgv") {
    return <CGVPage onBack={() => setCurrentView("home")} />;
  }

  if (currentView === "cgu") {
    return <CGUPage onBack={() => setCurrentView("home")} />;
  }

  if (currentView === "contact") {
    return <ContactPage onBack={() => setCurrentView("home")} />;
  }

  if (currentView === "confirmation") {
    return (
      <OrderConfirmation 
        items={cartItems} 
        onReturnHome={handleReturnHome}
        orderId={lastOrderId}
      />
    );
  }

  if (currentView === "checkout") {
    return (
      <Checkout
        items={cartItems}
        onBack={() => setCurrentView("home")}
        onComplete={handleCompleteOrder}
        onRemoveFromCart={handleRemoveFromCart}
      />
    );
  }

  if (currentView === "ticket-selection" && selectedEvent) {
    return (
      <TicketSelection
        event={selectedEvent}
        onBack={() => setCurrentView("event-details")}
        onAddToCart={handleAddToCart}
        cartItems={cartItems}
        onRemoveFromCart={handleRemoveFromCart}
        onCheckout={handleCheckout}
      />
    );
  }

  if (currentView === "event-details" && selectedEvent) {
    return (
      <EventDetails
        event={selectedEvent}
        onBack={() => {
          setCurrentView("home");
          setSelectedEventId(null);
          window.history.pushState({}, '', '/');
        }}
        onSelectTickets={handleSelectTickets}
        onAddToCart={handleAddToCart}
        cartItems={cartItems}
        onRemoveFromCart={handleRemoveFromCart}
        onCheckout={handleCheckout}
        onViewOrganizerPage={handleViewOrganizer}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background relative overflow-x-hidden">
      {/* SEO - Dynamic meta tags */}
      <SEO />
      
      {/* Subtle background images - shuffled randomly per visit */}
      <div className="fixed inset-0 z-0">
        <BackgroundGrid />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2a2520]/90 via-[#3d3832]/92 to-[#2a2520]/90"></div>
      </div>

      {/* Culture Background Wall */}
      <CultureBackground />

      {/* Header - Shotgun style: minimal and clean */}
      <header className="border-b border-white/5 sticky top-0 bg-background/80 backdrop-blur-xl z-50 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo Text + Calendar */}
            <div className="flex items-center gap-4">
              <h1 
                className="text-2xl font-bold tracking-wider text-white cursor-pointer" 
                onClick={handleLogoClick}
                data-testid="tika-logo"
              >
                TIKA
              </h1>
              
              <div className="h-6 w-px bg-white/10"></div>
              
              <CalendarButton events={events} />
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Organizer Button */}
              {currentOrganizer ? (
                <Button
                  data-testid="organizer-dashboard-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowOrganizerDashboard(true)}
                  className="flex items-center gap-1 sm:gap-2 border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-amber-200"
                >
                  <Building2 className="w-4 h-4" />
                  <span className="hidden sm:inline max-w-24 truncate">{currentOrganizer.company_name || currentOrganizer.name}</span>
                </Button>
              ) : (
                <Button
                  data-testid="organizer-login-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowOrganizerAuth(true)}
                  className="flex items-center gap-1 sm:gap-2 border-amber-500/30 hover:bg-amber-500/10 text-amber-200"
                >
                  <Building2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Organisateur</span>
                </Button>
              )}
              
              <Cart
                items={cartItems}
                onRemoveItem={handleRemoveFromCart}
                onCheckout={handleCheckout}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Shotgun style: minimal title */}
      <div className="relative z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="max-w-2xl">
            <h2 className="text-5xl sm:text-6xl font-bold tracking-wide mb-3 text-white">
              TIKA
            </h2>
            <p className="text-muted-foreground text-base">
              Hip-Hop/RNB • Afrobeat • Dancehall/Caraïbes
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 py-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          {/* Main Navigation Tabs - Shotgun style */}
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value)} className="mb-8">
            <TabsList className="bg-white/5 border-0 p-1 h-auto w-full sm:w-auto">
              <TabsTrigger 
                value="events" 
                data-testid="events-tab"
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-lg px-8 py-2.5 font-medium"
              >
                Événements
              </TabsTrigger>
              <TabsTrigger 
                value="culture" 
                data-testid="culture-tab"
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-lg px-8 py-2.5 font-medium"
              >
                Culture
              </TabsTrigger>
            </TabsList>

            {/* Events Tab Content */}
            <TabsContent value="events" className="space-y-8">
              {/* Filters - Responsive: horizontal scroll on mobile, inline on desktop */}
              <div className="space-y-4">
                {/* Search */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="search-input"
                      type="text"
                      placeholder="Rechercher..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-10 bg-white/5 border-white/10 rounded-full"
                    />
                  </div>
                  
                  {/* Advanced Filters */}
                  <EventFilters 
                    onFiltersChange={handleFiltersChange}
                    activeFiltersCount={Object.keys(activeFilters).length}
                  />
                </div>

                {/* Category filters - scrollable on mobile */}
                <div className="relative -mx-4 px-4 sm:mx-0 sm:px-0">
                  <div className="overflow-x-auto overflow-y-hidden pb-2 hide-scrollbar">
                    <Tabs value={selectedCategory} onValueChange={(value) => setSelectedCategory(value)}>
                      <TabsList className="bg-white/5 border-0 p-1 h-auto inline-flex flex-nowrap w-max sm:w-auto">
                        <TabsTrigger 
                          value="all" 
                          data-testid="filter-all" 
                          className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-4 sm:px-6 py-2 whitespace-nowrap text-sm"
                        >
                          Tous
                        </TabsTrigger>
                        <TabsTrigger 
                          value="Hip-Hop/RNB" 
                          data-testid="filter-hiphoprnb" 
                          className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-rose-500 data-[state=active]:text-white rounded-full px-4 sm:px-6 py-2 whitespace-nowrap text-sm"
                        >
                          Hip-Hop/RNB
                        </TabsTrigger>
                        <TabsTrigger 
                          value="Afrobeat" 
                          data-testid="filter-afrobeat" 
                          className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-teal-500 data-[state=active]:text-white rounded-full px-4 sm:px-6 py-2 whitespace-nowrap text-sm"
                        >
                          Afrobeat
                        </TabsTrigger>
                        <TabsTrigger 
                          value="Dancehall/Caraïbes" 
                          data-testid="filter-dancehall" 
                          className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-cyan-500 data-[state=active]:text-white rounded-full px-4 sm:px-6 py-2 whitespace-nowrap text-sm"
                        >
                          Dancehall/Caraïbes
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                  {/* Gradient fade indicator on right side for mobile */}
                  <div className="absolute right-0 top-0 bottom-2 w-12 bg-gradient-to-l from-background via-background/80 to-transparent pointer-events-none sm:hidden"></div>
                </div>
              </div>

              {/* Cultural Story - Minimal version */}
              <CulturalStory selectedCategory={selectedCategory} />

              {/* Events Grid - Shotgun style */}
              <div className="space-y-6">
                {loading ? (
                  <div className="text-center py-24">
                    <p className="text-muted-foreground">Chargement...</p>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredEvents.map((event) => (
                      <EventCard key={event.id} event={event} onViewDetails={handleViewDetails} />
                    ))}
                  </div>
                )}

                {!loading && filteredEvents.length === 0 && (
                  <div className="text-center py-24">
                    <p className="text-muted-foreground">Aucun événement trouvé</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Culture Tab Content */}
            <TabsContent value="culture">
              <CultureSection selectedCategory="all" />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Create Event Modal */}
      <CreateEvent
        isOpen={isCreateEventOpen}
        onClose={() => setIsCreateEventOpen(false)}
      />

      {/* Admin Auth Modal */}
      <AdminAuth
        isOpen={showAdminAuth}
        onClose={() => setShowAdminAuth(false)}
        onAuthenticated={handleAdminAuthenticated}
      />

      {/* Admin Dashboard */}
      <AdminDashboard
        isOpen={showAdminDashboard}
        onClose={() => setShowAdminDashboard(false)}
        onLogout={handleAdminLogout}
      />

      {/* User Auth Modal */}
      <UserAuth
        isOpen={showUserAuth}
        onClose={() => setShowUserAuth(false)}
        onAuthenticated={handleUserAuthenticated}
      />

      {/* User Profile Modal */}
      <UserProfile
        isOpen={showUserProfile}
        onClose={() => setShowUserProfile(false)}
        user={currentUser}
        onLogout={handleUserLogout}
      />

      {/* Organizer Auth Modal */}
      <OrganizerAuth
        isOpen={showOrganizerAuth}
        onClose={() => setShowOrganizerAuth(false)}
        onAuthenticated={handleOrganizerAuthenticated}
      />

      {/* Organizer Dashboard */}
      <OrganizerDashboard
        isOpen={showOrganizerDashboard}
        onClose={() => setShowOrganizerDashboard(false)}
        organizer={currentOrganizer}
        onLogout={handleOrganizerLogout}
        onUpdate={handleOrganizerUpdate}
      />

      {/* Footer */}
      <Footer onNavigate={handleFooterNavigate} />

      {/* Toast Notifications */}
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
